import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { listingStatusTabsStyles } from "../../../../../styles/listingStatusTabsSx";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  PERMISSION_PROJECT_OPTIONS,
  PermissionProject,
} from "../../../../../models/Employees/PermissionProject";
import { teamPermissionLabel } from "../../../../../models/Employees/TeamPermission";
import { TeamPermissionStore } from "../../../stores/TeamPermissionStore";

type TeamPermissionFiltersProps = {
  store: TeamPermissionStore;
};

const TeamPermissionFilters = ({ store }: TeamPermissionFiltersProps) => {
  const theme = useTheme();
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const teamOptions: FilterValue[] = store.teams.map((team) => ({
    id: String(team.id),
    value: `${teamPermissionLabel(team.name)} (${team.permissionCount})`,
  }));
  const permissionOptions: FilterValue[] = store.permissions.map(
    (permission) => ({
      id: String(permission.id),
      value: permission.name,
    }),
  );
  const selectedTeams = teamOptions.filter((option) =>
    store.selectedTeamIds.includes(Number(option.id)),
  );
  const selectedPermissions = permissionOptions.filter((option) =>
    store.selectedPermissionIds.includes(Number(option.id)),
  );

  const filtersAreActive =
    store.selectedTeamIds.length > 0 ||
    store.selectedPermissionIds.length > 0 ||
    store.searchDraft.trim().length > 0;

  return (
    <Box>
      <Tabs
        value={store.project}
        onChange={(_, value: PermissionProject) => {
          if (store.project === value) return;
          store.setProject(value);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={(theme) => ({ ...listingStatusTabsStyles(theme), mb: 2 })}
      >
        {PERMISSION_PROJECT_OPTIONS.map((project) => (
          <Tab
            key={project.id}
            label={project.value.toUpperCase()}
            value={project.id}
            disabled={
              store.loading ||
              store.saving ||
              (store.hasUnsavedChanges() && store.project !== project.id)
            }
            id={`team-permission-project-${project.id}`}
          />
        ))}
      </Tabs>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: "divider",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.5,
            borderBottom: isSearchExpanded ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          <Box
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            sx={{
              display: "flex",
              flex: 1,
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: 0,
              px: 1,
              py: 0.5,
              cursor: "pointer",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CustomTypography variant="heading6">Search</CustomTypography>
            <IconButton size="small" aria-label="Toggle search">
              {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          {!store.error && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
            >
              <AppButton
                variant="transparentPrimary"
                label="Reset"
                disabled={
                  !store.hasUnsavedChanges() || store.saving || store.loading
                }
                onClick={() => store.reset()}
              />
              <AppButton
                variant="primary"
                label={store.saving ? "Saving…" : "Save"}
                disabled={
                  !store.hasUnsavedChanges() || store.saving || store.loading
                }
                onClick={() => void store.save()}
              />
            </Box>
          )}
        </Box>
        <Collapse
          in={isSearchExpanded}
          timeout="auto"
          unmountOnExit
          sx={{ gridColumn: 1, minWidth: 0 }}
        >
          <Box sx={{ p: 2 }}>
            <Grid2 container spacing={1.5} sx={{ alignItems: "center" }}>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <CustomTextField
                  width="100%"
                  placeholder="Search permission"
                  value={store.searchDraft}
                  onChange={(event) => store.setSearchDraft(event.target.value)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <AppSearchDropdownMulti
                  title="Teams"
                  local
                  isMultiSelect
                  list={teamOptions}
                  value={selectedTeams}
                  handleSelect={(items) =>
                    store.setSelectedTeamIds(
                      items.map((item) => Number(item.id)),
                    )
                  }
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <AppSearchDropdownMulti
                  title="Permissions"
                  local
                  isMultiSelect
                  list={permissionOptions}
                  value={selectedPermissions}
                  handleSelect={(items) =>
                    store.setSelectedPermissionIds(
                      items.map((item) => Number(item.id)),
                    )
                  }
                />
              </Grid2>
            </Grid2>
            {filtersAreActive && (
              <Box sx={{ mt: 1.5 }}>
                <AppButton
                  variant="transparentPrimary"
                  label="Clear filters"
                  onClick={() => store.clearFilters()}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default observer(TeamPermissionFilters);
